import { Link } from 'react-router-dom';
import { Badge } from '../ui/Badge';
import { formatCurrency } from '../../lib/formatCurrency';

const CATEGORY_COLORS: Record<string, string> = {
  music: 'bg-green-500',
  games: 'bg-teal-500',
  'other-tools': 'bg-blue-500',
  experiments: 'bg-purple-500',
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => {
        const filled = rating >= star;
        const half = !filled && rating >= star - 0.5;
        return (
          <svg key={star} className={`h-3.5 w-3.5 ${filled ? 'text-yellow-400' : half ? 'text-yellow-400' : 'text-gray-600'}`} fill="currentColor" viewBox="0 0 24 24">
            {filled || half ? (
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            ) : (
              <path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z" />
            )}
          </svg>
        );
      })}
    </div>
  );
}

interface ProductCardProps {
  name: string;
  slug: string;
  price: number | null;
  thumbnailUrl: string | null;
  isFeatured?: boolean;
  isComingSoon: boolean;
  categoryName?: string;
  categorySlug?: string;
  reviewCount?: number;
  rating?: number;
}

export function ProductCard({ name, slug, price, thumbnailUrl, isFeatured, isComingSoon, categoryName, categorySlug, reviewCount, rating }: ProductCardProps) {
  const badgeColor = categorySlug ? CATEGORY_COLORS[categorySlug] || 'bg-gray-500' : '';

  return (
    <Link
      to={`/product/${slug}`}
      className="glass-card glow-hover group block rounded-xl overflow-hidden transition-all duration-300"
      data-testid="product-card"
      data-product-slug={slug}
    >
      <div className="relative aspect-video bg-navy-800/50 overflow-hidden">
        {thumbnailUrl ? (
          <img src={thumbnailUrl} alt={name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="flex h-full items-center justify-center">
            <svg className="h-12 w-12 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>
          </div>
        )}
        {/* Category badge overlay */}
        {categoryName && (
          <span className={`absolute top-3 left-3 rounded-full px-2.5 py-0.5 text-xs font-semibold text-white ${badgeColor}`}>
            {categoryName}
          </span>
        )}
        {/* Featured / Coming Soon badges */}
        <div className="absolute top-3 right-3 flex gap-1.5">
          {isFeatured && <Badge variant="success">Featured</Badge>}
          {isComingSoon && <Badge variant="warning">Coming Soon</Badge>}
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-display font-semibold text-white group-hover:text-green-400 transition-colors">{name}</h3>
        {rating !== undefined && (
          <div className="mt-1.5">
            <StarRating rating={rating} />
          </div>
        )}
        <div className="mt-3 flex items-center justify-between">
          <div>
            {!isComingSoon && <span className="font-display text-lg font-bold text-gradient-green">{price ? formatCurrency(price) : 'Free'}</span>}
          </div>
          <div className="flex items-center gap-3">
            {reviewCount !== undefined && <span className="text-xs text-gray-500">{reviewCount} reviews</span>}
            {!isComingSoon && price != null && (
              <button
                aria-label="Add to cart"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                className="rounded-lg bg-white/5 p-2 text-gray-400 transition-all hover:bg-green-500/20 hover:text-green-400"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" /></svg>
              </button>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
}
